import { Trans } from '@lingui/react/macro'
import { collectSubjectCitations } from 'features/facilityModel/citations'
import { kindLabel } from 'features/facilityModel/columns'
import {
  buildSubjectReferenceList,
  type SubjectReference,
} from 'features/facilityModel/referenceList'
import { useDocumentTitles } from 'features/facilityModel/useDocumentTitles'
import {
  useOpenCitationDocument,
  useOpenSubjectResource,
} from 'features/facilityModel/useFacilityModelNavigation'
import type { FimSubjectRecord, FimSubjectSummary } from 'features/fim'
import { useFimSubjectsByIds } from 'features/fim'
import { isPidDocument } from 'features/fimEvidence'
import { KnowledgeDocumentDataProvider } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { PipelineConfigProvider } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { FC, useMemo } from 'react'
import styled from 'styled-components'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.25rem 0.25rem 0.375rem 1.125rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

const SubjectBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;

  & + & {
    margin-top: 0.25rem;
  }
`

const SubjectHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  min-width: 0;
`

const SubjectNameButton = styled.button`
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
`

const SubjectKindLabel = styled.span`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
  flex-shrink: 0;
`

const ReferenceRow = styled.button`
  display: block;
  width: 100%;
  padding: 0.0625rem 0;
  margin: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.55rem;
  line-height: 1.2;
  text-align: left;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
    text-decoration: underline;
  }
`

const StateRow = styled.div`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const pidFirst = (references: readonly SubjectReference[]) =>
  [...references].sort(
    (a, b) =>
      Number(isPidDocument({ title: b.label })) -
      Number(isPidDocument({ title: a.label }))
  )

interface PenSubjectLinksContentProps {
  siteID: string
  subjects: FimSubjectSummary[]
}

const PenSubjectLinksContent: FC<PenSubjectLinksContentProps> = ({
  siteID,
  subjects,
}) => {
  const uids = useMemo(
    () => subjects.map((subject) => subject.subjectUid),
    [subjects]
  )
  const {
    subjects: fullSubjects,
    isLoading,
    error,
  } = useFimSubjectsByIds(siteID, uids)

  const fullByUid = useMemo(() => {
    const map = new Map<string, FimSubjectRecord>()
    for (const full of fullSubjects) map.set(full.subjectUid, full)
    return map
  }, [fullSubjects])

  const baseCitationsByUid = useMemo(() => {
    const map = new Map<string, ReturnType<typeof collectSubjectCitations>>()
    for (const full of fullSubjects) {
      map.set(full.subjectUid, collectSubjectCitations(full))
    }
    return map
  }, [fullSubjects])

  const allDocumentIds = useMemo(() => {
    const ids = new Set<string>()
    for (const citations of baseCitationsByUid.values()) {
      for (const citation of citations) ids.add(citation.documentId)
    }
    return [...ids]
  }, [baseCitationsByUid])

  const { titleFor } = useDocumentTitles(allDocumentIds)
  const openSubject = useOpenSubjectResource(siteID)
  const openCitation = useOpenCitationDocument(siteID)

  if (isLoading) {
    return (
      <StateRow>
        <Trans>Loading…</Trans>
      </StateRow>
    )
  }

  return (
    <>
      {subjects.map((summary) => {
        const full = fullByUid.get(summary.subjectUid)
        const references = full
          ? buildSubjectReferenceList(
              collectSubjectCitations(full, titleFor),
              null
            )
          : []
        const ordered = pidFirst(references)

        return (
          <SubjectBlock key={summary.subjectUid}>
            <SubjectHeaderRow>
              <SubjectNameButton
                type='button'
                title={summary.data.name}
                onClick={() =>
                  openSubject(summary.subjectUid, summary.data.name)
                }>
                {summary.data.name}
              </SubjectNameButton>
              <SubjectKindLabel>
                {kindLabel(summary.data.kind)}
              </SubjectKindLabel>
            </SubjectHeaderRow>
            {error || ordered.length === 0 ? (
              <StateRow>
                <Trans>No referenced documents</Trans>
              </StateRow>
            ) : (
              ordered.map((reference) => (
                <ReferenceRow
                  key={reference.documentId}
                  type='button'
                  title={reference.label}
                  onClick={() =>
                    openCitation({
                      documentId: reference.documentId,
                      page: reference.page,
                      boxes: reference.boxes,
                      label: reference.label,
                    })
                  }>
                  {reference.label}
                </ReferenceRow>
              ))
            )}
          </SubjectBlock>
        )
      })}
    </>
  )
}

/**
 * A pen row's linked Subject(s), rendered inline in the drawer list directly
 * beneath the row: identity, kind, and referenced documents (P&IDs surfaced
 * first), each opening the Subject page or the cited document. Mounted only
 * while its disclosure is expanded — conditional render is what makes the
 * subject fetch lazy, the same way Popover's mount-on-open did before.
 */
export const PenSubjectLinksSection: FC<{
  siteID: string
  subjects: FimSubjectSummary[]
}> = ({ siteID, subjects }) => (
  <Section
    // sits inside the drawer list; clicks inside it must not toggle the
    // pen's visibility (the row above already owns that)
    onClick={(event) => event.stopPropagation()}>
    <PipelineConfigProvider siteID={siteID} forceActiveConfig>
      <KnowledgeDocumentDataProvider siteID={siteID} orgID=''>
        <PenSubjectLinksContent siteID={siteID} subjects={subjects} />
      </KnowledgeDocumentDataProvider>
    </PipelineConfigProvider>
  </Section>
)
